/**
 * Global location search — the map toolbar control.
 *
 * Replaces a placeholder-only input that had no value, no change handler, and
 * no service behind it, which is why searching never did anything.
 *
 * Behaviour:
 *  - suggestions appear WHILE TYPING (no Enter required), after a debounce
 *  - every keystroke aborts the previous request, so a slow early response can
 *    never overwrite newer suggestions
 *  - full keyboard support: ↑ ↓ Enter Escape
 *  - explicit idle / loading / results / empty / error states
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, MapPin, Search, X } from 'lucide-react';
import {
  getResultView,
  locationSearchService,
  type LocationResult,
} from '../../services/locationSearchService';

const DEBOUNCE_MS = 300;
const MIN_QUERY = 2;

type Status = 'idle' | 'loading' | 'ready' | 'error';

interface Props {
  /** Moves the map to the chosen place. */
  onGoToLocation: (center: [number, number], zoom: number) => void;
  /** Drops/updates the temporary marker; called with null when cleared. */
  onMarkerChange?: (marker: { latitude: number; longitude: number; label: string } | null) => void;
  className?: string;
}

/** Highlights the typed substring inside a suggestion. */
function Highlighted({ text, query }: { text: string; query: string }) {
  const index = text.toLowerCase().indexOf(query.toLowerCase());
  if (!query || index < 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, index)}
      <mark className="bg-transparent font-semibold text-blue-700">
        {text.slice(index, index + query.length)}
      </mark>
      {text.slice(index + query.length)}
    </>
  );
}

export default function GlobalLocationSearch({
  onGoToLocation,
  onMarkerChange,
  className = '',
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<LocationResult[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  /* ── Debounced search ─────────────────────────────────────────────────
     The timer is cleared and the in-flight request aborted on every change,
     which handles both "don't fetch per keystroke" and "ignore stale replies". */
  useEffect(() => {
    const trimmed = query.trim();

    if (trimmed.length < MIN_QUERY) {
      abortRef.current?.abort();
      setResults([]);
      setStatus('idle');
      setError(null);
      return undefined;
    }

    const timer = window.setTimeout(() => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setStatus('loading');
      setError(null);

      locationSearchService
        .search(trimmed, controller.signal)
        .then((found) => {
          if (controller.signal.aborted) return;
          setResults(found);
          setActiveIndex(found.length > 0 ? 0 : -1);
          setStatus('ready');
        })
        .catch((caught: unknown) => {
          // An abort is the expected outcome of typing again; not an error.
          if (controller.signal.aborted || (caught as Error)?.name === 'AbortError') return;
          console.error('[search] location search failed', caught);
          setError(caught instanceof Error ? caught.message : 'Unable to search locations');
          setStatus('error');
        });
    }, DEBOUNCE_MS);

    return () => window.clearTimeout(timer);
  }, [query]);

  // Abort anything still in flight when the control unmounts.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (rootRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  const choose = useCallback(
    (result: LocationResult) => {
      const view = getResultView(result);
      onGoToLocation(view.center, view.zoom);
      onMarkerChange?.({
        latitude: result.latitude,
        longitude: result.longitude,
        label: result.displayName,
      });
      setQuery(result.displayName);
      setResults([]);
      setStatus('idle');
      setOpen(false);
    },
    [onGoToLocation, onMarkerChange],
  );

  const reset = () => {
    abortRef.current?.abort();
    setQuery('');
    setResults([]);
    setStatus('idle');
    setError(null);
    onMarkerChange?.(null);
    inputRef.current?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && results.length > 0) {
      event.preventDefault();
      setActiveIndex((current) => (current + 1) % results.length);
    } else if (event.key === 'ArrowUp' && results.length > 0) {
      event.preventDefault();
      setActiveIndex((current) => (current - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const picked = results[activeIndex] ?? results[0];
      if (picked) choose(picked);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      if (results.length > 0) {
        setResults([]);
        setStatus('idle');
      } else {
        setOpen(false);
      }
    }
  };

  const showPanel = open && query.trim().length >= MIN_QUERY;

  return (
    <div ref={rootRef} className={`relative flex items-center gap-2 ${className}`}>
      {open && (
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Search location…"
            aria-label="Search location"
            role="combobox"
            aria-expanded={showPanel}
            aria-controls="location-search-results"
            aria-autocomplete="list"
            className="gl-input w-56 py-2 pl-8 pr-7 text-sm sm:w-64"
          />
          {query && (
            <button
              type="button"
              onClick={reset}
              aria-label="Clear search"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
            >
              <X className="h-3 w-3" />
            </button>
          )}

          {showPanel && (
            <div
              id="location-search-results"
              role="listbox"
              aria-label="Location suggestions"
              className="gl-menu absolute left-0 top-full z-[6000] mt-1.5 max-h-72 w-[19rem] overflow-y-auto py-1"
            >
              {status === 'loading' && (
                <p className="flex items-center gap-2 px-3 py-2.5 text-xs text-slate-500">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Searching…
                </p>
              )}

              {status === 'error' && (
                <div className="px-3 py-2.5">
                  <p className="text-xs font-medium text-red-600">Unable to search locations.</p>
                  <p className="mt-0.5 text-[11px] text-slate-400">{error} — please try again.</p>
                </div>
              )}

              {status === 'ready' && results.length === 0 && (
                <p className="px-3 py-2.5 text-xs text-slate-500">No locations found.</p>
              )}

              {status === 'ready' &&
                results.map((result, index) => (
                  <button
                    key={result.id}
                    type="button"
                    role="option"
                    aria-selected={index === activeIndex}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => choose(result)}
                    className={`flex w-full items-start gap-2 px-3 py-2.5 text-left transition ${
                      index === activeIndex ? 'bg-[var(--glass-hover)]' : ''
                    }`}
                  >
                    <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-blue-600" />
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium text-slate-800">
                        <Highlighted text={result.label} query={query.trim()} />
                      </span>
                      <span className="block truncate text-[11px] text-slate-400">
                        {[result.city, result.state, result.country].filter(Boolean).join(', ') ||
                          result.displayName}
                      </span>
                    </span>
                  </button>
                ))}
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          setOpen((value) => !value);
          if (open) setResults([]);
        }}
        // Distinct from the input's own "Search location" label — two controls
        // sharing one accessible name is ambiguous for screen readers.
        aria-label={open ? 'Close location search' : 'Open location search'}
        aria-pressed={open}
        title={open ? 'Close location search' : 'Open location search'}
        className="gl-btn gl-btn--ghost p-2"
      >
        <Search className="h-4 w-4" />
      </button>
    </div>
  );
}
